import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { viewportConfig } from './animations';
import {
  ArrowRight,
  Check,
  Server,
  Cloud,
  Coins,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export function PricingSection() {
  const { t } = useTranslation();

  const { data: pricing } = useQuery({
    queryKey: ['pricing'],
    queryFn: async () => {
      const { data } = await api.get('/billing/pricing');
      return data;
    },
  });

  const byosPrice = pricing?.byos?.priceOneTime || 5;
  const managedPrice = pricing?.managed?.priceMonthly || 10;

  const plans = [
    {
      id: 'byos',
      name: t('landing.pricing.byos.name'),
      price: byosPrice,
      oneTime: true,
      icon: Server,
      color: 'text-orange-500',
      bg: 'bg-orange-500/10',
      borderColor: 'border-orange-500/30',
      hoverBorder: 'hover:border-orange-500/60',
      description: t('landing.pricing.byos.description'),
      features: [
        t('landing.pricing.byos.feature1'),
        t('landing.pricing.byos.feature2'),
        t('landing.pricing.byos.feature3'),
        t('landing.pricing.byos.feature4'),
        t('landing.pricing.byos.feature5'),
        t('landing.pricing.byos.feature6'),
      ],
    },
    {
      id: 'managed',
      name: t('landing.pricing.managed.name'),
      price: managedPrice,
      icon: Cloud,
      color: 'text-emerald-500',
      bg: 'bg-emerald-500/10',
      borderColor: 'border-emerald-500/30',
      hoverBorder: 'hover:border-emerald-500/60',
      description: t('landing.pricing.managed.description'),
      features: [
        t('landing.pricing.managed.feature1'),
        t('landing.pricing.managed.feature2'),
        t('landing.pricing.managed.feature3'),
        t('landing.pricing.managed.feature4'),
        t('landing.pricing.managed.feature5'),
        t('landing.pricing.managed.feature6'),
        t('landing.pricing.managed.feature7'),
      ],
    },
    {
      id: 'managedWallet',
      name: t('landing.pricing.managedWallet.name'),
      price: managedPrice,
      popular: true,
      icon: Coins,
      color: 'text-emerald-500',
      bg: 'bg-emerald-500/10',
      borderColor: 'border-emerald-500',
      hoverBorder: 'hover:border-emerald-400',
      description: t('landing.pricing.managedWallet.description'),
      priceSuffix: t('landing.pricing.plusFunds'),
      features: [
        t('landing.pricing.managedWallet.feature1'),
        t('landing.pricing.managedWallet.feature2'),
        t('landing.pricing.managedWallet.feature3'),
        t('landing.pricing.managedWallet.feature4'),
        t('landing.pricing.managedWallet.feature5'),
        t('landing.pricing.managedWallet.feature6'),
        t('landing.pricing.managedWallet.feature7'),
      ],
    },
  ];

  return (
    <section id="pricing" className="py-20 md:py-28">
      <div className="container mx-auto px-4">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewportConfig}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('landing.pricing.title')}</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t('landing.pricing.subtitle')}</p>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={viewportConfig}
              transition={{ delay: i * 0.15, duration: 0.6 }}
            >
              <Card className={`relative h-full overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 ${plan.hoverBorder} ${plan.popular ? `border-2 ${plan.borderColor} shadow-lg` : 'border'}`}>
                {/* Gradient border glow for popular */}
                {plan.popular && (
                  <div className="absolute inset-0 rounded-[inherit] bg-gradient-to-b from-emerald-500/10 to-teal-500/5 pointer-events-none" />
                )}
                {plan.popular && (
                  <div className="absolute top-0 right-0 z-10">
                    <div className="relative bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-xs font-semibold px-4 py-1.5 rounded-bl-xl overflow-hidden">
                      {t('landing.pricing.popular')}
                      {/* Shimmer effect */}
                      <div className="absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                    </div>
                  </div>
                )}
                <CardContent className="p-8 relative">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`inline-flex items-center justify-center h-12 w-12 rounded-xl ${plan.bg} ${plan.color}`}>
                      <plan.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">{plan.name}</h3>
                      <p className="text-sm text-muted-foreground">{plan.description}</p>
                    </div>
                  </div>

                  <div className="flex items-baseline gap-1 mb-2">
                    <span className="text-5xl font-extrabold">${plan.price}</span>
                    <span className="text-muted-foreground text-lg">
                      {plan.oneTime ? ` ${t('landing.pricing.oneTime')}` : `/${t('landing.pricing.monthly')}`}
                    </span>
                    {plan.priceSuffix && <span className="text-sm text-muted-foreground ml-1">{plan.priceSuffix}</span>}
                  </div>
                  <p className="text-sm text-muted-foreground mb-6">{t('landing.pricing.perInstance')}</p>

                  <div className="border-t pt-6 mb-6">
                    <p className="text-sm font-semibold mb-3">{t('landing.pricing.includes')}</p>
                    <ul className="space-y-3">
                      {plan.features.map((feature, j) => (
                        <motion.li
                          key={j}
                          className="flex items-start gap-3 text-sm"
                          initial={{ opacity: 0, x: -10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.3 + j * 0.05 }}
                        >
                          <Check className={`h-4 w-4 mt-0.5 flex-shrink-0 ${plan.color}`} />
                          <span>{feature}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  <Link to="/register">
                    <Button
                      className={`w-full h-12 text-base font-semibold ${plan.popular ? 'shadow-lg shadow-emerald-500/25' : ''}`}
                      variant={plan.popular ? 'default' : 'outline'}
                    >
                      {t('landing.pricing.getStarted')}
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
